import styled, { css } from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";

export const SlideRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 4px 32px;
`;

/** 머리 줄 하나(제목·환자·저장 상태·자체 닫기 — 560px에서 한 줄, 화면 설계 3-1). */
export const HeaderRow1 = styled.div`
  position: sticky; /* 본문을 스크롤해도 머리 줄은 고정(사용자 지시 2026-10-13) — 스크롤 컨테이너는 SlidePopup의 SlideContent */
  top: 0;
  z-index: 5;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  padding: 4px 0 10px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
`;

/** 머리 줄 오른쪽 닫기 — 허가사항 슬라이드(SlidePopup 기본 닫기)와 같은 큰 동그라미 안 X. */
export const CloseCircle = styled.div`
  flex-shrink: 0;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** 머리 줄 아래 하위 칸 묶음(「복약지도한 날」·「보고서」) — 칸끼리는 6px로 붙여 머리 줄과 위계가 보이게 한다. */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
`;

export const HeaderTitle = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
  white-space: nowrap;
`;

/** 머리 줄의 환자 맥락(`환자 {id} · {나이}세 {성별} · 메모 {memo}` — 결정 20). 긴 메모는 한 줄 말줄임. */
export const PatientText = styled.span`
  min-width: 0;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 메모 아래의 목적 한 줄(흐린 글씨 — 결정 20, 2026-10-14 사용자 결정으로 머리 아래에서 메모 아래로). */
export const PurposeText = styled.div`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.4;
  color: ${COLOR_SYSTEM.get("Gray")[400]};
`;

/** O·X 설명 블록 — DRP 표 앞에 한 번(설계 결정 21, 사용자 문구 결정 2026-10-14). */
export const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
  font-size: 14px;
  line-height: 1.5;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
`;
export const LegendLead = styled.div`
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
`;

/** 체크 표의 행 — O 행은 연한 파랑, X 행은 연한 회색 + 흐린 글자, 빈 칸 행은 흰 배경(사용자 결정 2026-10-14). 클릭 핸들러 없음, hover는 읽기 보조로만. */
export const CheckRow = styled.tr<{ $guidance?: "done" | "not_needed" }>`
  background: ${({ $guidance }) =>
    $guidance === "done"
      ? (COLOR_SYSTEM.get("Blue")?.[50] ?? "#E3F2FD")
      : $guidance === "not_needed"
        ? COLOR_SYSTEM.get("Gray")[50]
        : "transparent"};
  & > td {
    color: ${({ $guidance }) =>
      $guidance === "not_needed" ? COLOR_SYSTEM.get("Gray")[400] : "inherit"};
  }
  /* 행 hover(읽기 보조, 사용자 지시 2026-10-14 — 클릭 동작·pointer 커서는 없다): 빈 칸 행은 Skyblue 10(사용자 지시 — O 행 Blue 50과 가까워 한 단계 연하게), O 행 Blue 100, X 행 Gray 100. */
  transition: background-color 0.12s ease;
  &:hover {
    background: ${({ $guidance }) =>
      $guidance === "done"
        ? (COLOR_SYSTEM.get("Blue")?.[100] ?? "#BBDEFB")
        : $guidance === "not_needed"
          ? (COLOR_SYSTEM.get("Gray")?.[100] ?? "#E0E0E0")
          : COLOR_SYSTEM.get("Skyblue")[10]};
  }
`;

export const SaveStatusText = styled.span<{ $failed?: boolean }>`
  font-size: 13px;
  color: ${({ $failed }) =>
    $failed ? COLOR_SYSTEM.get("Red")[500] : COLOR_SYSTEM.get("Gray")[400]};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

/** 머리 날짜 입력의 기본 틀(높이·글꼴·테두리 — 화면 설계 3-1 "같은 모양의 라벨 칸"). */
const fieldControl = css`
  box-sizing: border-box;
  height: 32px;
  padding: 0 8px;
  font: inherit;
  font-size: 13px;
  line-height: 30px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  border-radius: 6px;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
  background: #fff;
`;

export const DateInput = styled.input`
  ${fieldControl}
`;

/** 읽기 화면의 복약지도한 날(수정 불가, 텍스트). */
export const DateStatic = styled.span`
  font-size: 13px;
  line-height: 32px;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
`;

/** 머리 라벨 칸(「복약지도한 날」·「보고서」 — 라벨 폭 고정, 세로 정렬, 화면 설계 3-1). */
export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FieldLabel = styled.span`
  flex: 0 0 88px;
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
  white-space: nowrap;
`;

export const FieldValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 32px;
  flex-wrap: wrap;
  font-size: 13px;
  line-height: 1.4;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
`;

/** 값이 오늘(KST)이면 날짜 옆 작은 태그(결정 9). */
export const TodayTag = styled.span`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Green")[600]};
  background: ${COLOR_SYSTEM.get("Green")[50]};
  white-space: nowrap;
`;

/** 「보고서」 칸 끝의 흐린 꼬리(「· 수정본 기준」). */
export const DimTail = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")[400]};
`;

/** 라벨 칸 아래 보조 한 줄(값 열에 맞춰 들여쓰기). 라벨 폭 88px + 간격 8px. */
export const FieldNote = styled.div`
  margin-left: 96px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")[400]};
  line-height: 1.4;
`;

export const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${COLOR_SYSTEM.get("Yellow")?.[50] ?? "#FFF8E1"};
  border: 1px solid ${COLOR_SYSTEM.get("Yellow")?.[200] ?? "#FFE082"};
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
`;

export const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
`;

export const GuideText = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")[400]};
`;

export const CheckTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    border: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
    padding: 8px;
    text-align: left;
    vertical-align: top;
    word-break: break-word;
  }
  th {
    background: ${COLOR_SYSTEM.get("Gray")[10]};
    font-weight: 600;
    white-space: nowrap;
  }
  /* 첫 열(복약지도) 폭 72px — O·X 버튼 28px 둘 + 간격 4px + 좌우 여백 6px(설계 결정 21 · 화면 설계 3-2). */
  th:first-child,
  td:first-child {
    box-sizing: border-box;
    width: 72px;
    padding-left: 6px;
    padding-right: 6px;
    white-space: nowrap;
  }
  /* 행에는 클릭 핸들러·pointer 커서를 두지 않는다(설계 결정 21, 사용자 결정 2026-10-14). hover 배경은 CheckRow. */
`;

/** 복약지도 칸의 O·X 버튼 묶음 — 각 28×28px, 간격 4px. 꺼짐은 둘 다 같은 회색 테두리(Tertiary Outlined), 켜짐은 채움(O 파랑 Primary, X 진회색 Secondary) — 꺼진 O가 파란 테두리면 전부 O로 보인다(사용자 지적 2026-10-14). */
export const GuidanceButtons = styled.div`
  display: inline-flex;
  gap: 4px;

  & > button {
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
    font-weight: 700;
    transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  }
  /* hover(사용자 지시 2026-10-14): 꺼진 O는 연한 파랑 + 파란 글자·테두리, 꺼진 X는 연한 회색 + 진회색 글자, 켜진 쪽은 한 단계 진하게. */
  & > button[data-testid="guidance-done"]:not([aria-pressed="true"]):hover {
    background: ${COLOR_SYSTEM.get("Blue")?.[50] ?? "#E3F2FD"};
    color: ${COLOR_SYSTEM.get("Blue")[400]};
    border-color: ${COLOR_SYSTEM.get("Blue")[400]};
  }
  & > button[data-testid="guidance-done"][aria-pressed="true"]:hover {
    background: ${COLOR_SYSTEM.get("Blue")?.[500] ?? COLOR_SYSTEM.get("Blue")[400]};
  }
  & > button[data-testid="guidance-not-needed"]:not([aria-pressed="true"]):hover {
    background: ${COLOR_SYSTEM.get("Gray")[50]};
    color: ${COLOR_SYSTEM.get("Gray")[700]};
    border-color: ${COLOR_SYSTEM.get("Gray")[200]};
  }
  & > button[data-testid="guidance-not-needed"][aria-pressed="true"]:hover {
    background: ${COLOR_SYSTEM.get("Gray")[800]};
  }
`;

/** 읽기 셀의 O / X 글자(빈 칸은 글자 없음). */
export const GuidanceMark = styled.span<{ $guidance?: "done" | "not_needed" }>`
  display: inline-block;
  min-width: 28px;
  font-weight: 700;
  text-align: center;
  color: ${({ $guidance }) =>
    $guidance === "done" ? COLOR_SYSTEM.get("Blue")[400] : COLOR_SYSTEM.get("Gray")[500]};
`;

export const NewBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Blue")?.[600] ?? "#1565C0"};
  background: ${COLOR_SYSTEM.get("Blue")?.[50] ?? "#E3F2FD"};
`;

export const NoteArea = styled.textarea`
  width: 100%;
  min-height: 132px;
  max-height: 40vh;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  font-family: inherit;
  color: ${COLOR_SYSTEM.get("Gray")[800]};

  &::placeholder {
    color: ${COLOR_SYSTEM.get("Gray")[300]};
  }
`;

export const EmptyNotice = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
`;

/** 읽기 화면의 메모(평문, 줄바꿈 보존 — MD 렌더 아님). */
export const NoteRead = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  padding: 10px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
  min-height: 48px;
`;

/** 읽기 화면 바닥 버튼(수정·삭제). */
export const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
`;
